import { useCallback, useRef } from 'react';
import { callBackend } from '../../../utils/api';
import {
    buildSelectedOverviewRefreshContextKey,
    normalizePriceRefreshStatusPayload,
    isReadyWatchlistPublicationRefreshResult,
} from '../dashboardUtils';
import {
    buildRiskOverviewScopeKey,
    buildSelectedRiskOverviewScopeKey,
    createRiskOverviewSnapshotPublicationTransaction,
} from '../riskOverviewResource';
import { buildRiskRequestKey } from '../orchestration/requestRegistry';
import useSingleFlightRequests from './useSingleFlightRequests';
import { buildPortfolioRiskOverviewScope } from '../orchestration/publicationVersionTransition';
import { createPublicationStateTransaction } from '../orchestration/publicationStateTransaction';

const DEFERRED_STARTUP_TIMEOUT_MS = 1000;
const MAX_ACTUAL_SNAPSHOT_ATTEMPTS = 3;
const WATCHLIST_PUBLICATION_OPERATION_TYPES = new Set([
    'watchlist_update',
    'watchlist_upload',
    'security_resolution_apply',
]);

const waitForDeferredStartup = () => new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(resolve, { timeout: DEFERRED_STARTUP_TIMEOUT_MS });
        return;
    }
    setTimeout(resolve, 0);
});

const buildWatchlistSurfaceResult = ({
    rows,
    watchlistRevision = null,
    matricesOverride = null,
    matrixStatus = 'not_requested',
} = {}) => {
    if (!Array.isArray(rows)) {
        return {
            source: 'not_loaded',
            riskLoaded: false,
            rowCount: 0,
            fallbackReason: null,
            matrixStatus,
            watchlistRevision,
        };
    }

    const metadataFallbackReason = rows.find((row) => (
        row?._matrixBackedRiskLoaded === false
    ))?._riskDataFallbackReason;
    if (metadataFallbackReason || (!matricesOverride && rows.length > 0)) {
        return {
            source: 'metadata_fallback',
            riskLoaded: false,
            rowCount: rows.length,
            fallbackReason: metadataFallbackReason || 'matrix_unavailable',
            matrixStatus,
            watchlistRevision,
        };
    }

    if (rows.length === 0) {
        return {
            source: 'empty',
            riskLoaded: false,
            rowCount: 0,
            fallbackReason: null,
            matrixStatus,
            watchlistRevision,
        };
    }

    return {
        source: matricesOverride ? 'matrix' : 'metadata_fallback',
        riskLoaded: Boolean(matricesOverride),
        rowCount: rows.length,
        fallbackReason: matricesOverride ? null : 'matrix_unavailable',
        matrixStatus,
        watchlistRevision,
    };
};

const buildNotLoadedWatchlistSurface = (matrixStatus = 'not_requested') => ({
    source: 'not_loaded',
    riskLoaded: false,
    rowCount: 0,
    fallbackReason: null,
    matrixStatus,
    watchlistRevision: null,
});

const buildCoreLoadResult = ({
    actualId = null,
    matricesOverride = null,
    priceRefreshStatus = null,
    watermarkAdvancedDuringLoad = false,
    matrixStatus = 'not_requested',
    matrixReason = null,
    watchlistSurface = buildNotLoadedWatchlistSurface(matrixStatus),
    deferredCompletion = null,
    prepared = null,
    preparedState = null,
    prepareSelectedOverview = null,
} = {}) => ({
    actualId,
    matricesOverride,
    priceRefreshStatus,
    watermarkAdvancedDuringLoad,
    matrixStatus,
    matrixReason,
    watchlistSurface,
    deferredCompletion,
    ...(prepared ? { prepared, preparedState, prepareSelectedOverview } : {}),
});

const normalizeMatrixWorkflowResult = (result) => {
    if (
        !result
        || typeof result !== 'object'
        || !Object.prototype.hasOwnProperty.call(result, 'matrixLoadStatus')
    ) {
        throw new Error('Factor matrix workflow returned an invalid result.');
    }
    return {
        response: result.response ?? null,
        matrixStatus: result.matrixLoadStatus,
        matrixReason: result.matrixLoadReason ?? null,
    };
};

export function buildMatrixContextKey({
    userUid,
    selectedFactorModel,
    requestedFactorDataModel,
    matrixScopeVersion,
    watchlistIdentityScopeVersion,
    latestFactorDataVersionKey,
    factorVendorOverride,
    factorDataModelOverride,
    factorSnapshotDateOverride,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
} = {}) {
    return [
        userUid ?? '',
        (factorVendorOverride ?? selectedFactorModel) ?? '',
        (factorDataModelOverride ?? requestedFactorDataModel) ?? '',
        matrixScopeVersion ?? '',
        watchlistIdentityScopeVersion ?? '',
        latestFactorDataVersionKey || (
            factorSnapshotDateOverride !== undefined
                ? factorSnapshotDateOverride
                : factorSnapshotDate
        ) || '',
        idioVolSource ?? '',
        betaSource ?? '',
    ].map((value) => String(value)).join('|');
}

export default function useCoreLoadOrchestrator({
    userUid,
    currentIdioVolSource = 'standard',
    currentBetaSource = 'yahoo',
    watchlistVersion,
    posSizingVersion = 0,
    selectedOverviewInvalidationVersionRef,
    watchlistIdentityScopeVersion = '',
    latestFactorDataVersionKey = '',
    actualPortfolioVersion,
    selectedFactorModel,
    requestedFactorDataModel,
    factorSnapshotDate,
    optimalPortfolioSummaryId,
    optimalPortfolioSummary,
    selectedSummaryPortfolioMeta,
    listOfWhatIfPortfolios,
    matrixContextKeyRef,
    coreLoadRequestIdRef,
    exclusiveCoreLoadOwnerRef,
    lastAppliedPriceWatermarkRef,
    lastPendingPriceWatermarkRef,
    lastSeenOptimizedOverviewRefreshRef,
    selectedOverviewRefreshRequestIdRef,
    coreSelectedOverviewOwnerRef,
    fetchIndustries,
    fetchOptimizerPortfolioSummaries,
    fetchPositions,
    fetchHoldings,
    fetchGroupRiskMetrics,
    fetchFactorMatrices,
    buildFactorContextFromResponse,
    getCurrentFactorContext,
    fetchActualPortfolioId,
    fetchSecurityQuotesForPublication,
    clearActualRiskOverviewState,
    getPortfolioOverviewRiskContext,
    isPosSizingPortfolioSelection,
    fetchOptimizedRiskMetrics,
    setOptimalPortfolioSummaryLoaded,
    setPriceRefreshStatus,
    setPendingAutoRefresh,
    onCoreFactorContextCommitted = null,
}) {
    const runSingleFlight = useSingleFlightRequests();
    const coreLoadRequestOwnerRef = useRef({ requestId: null, owner: null });
    const isCoreLoadCurrent = useCallback(
        (requestId) => {
            if (
                requestId == null
                || coreLoadRequestIdRef.current !== requestId
                || coreLoadRequestOwnerRef.current.requestId !== requestId
            ) return false;
            const requestOwner = coreLoadRequestOwnerRef.current.owner;
            return requestOwner
                ? exclusiveCoreLoadOwnerRef?.current === requestOwner
                : !exclusiveCoreLoadOwnerRef?.current;
        },
        [coreLoadRequestIdRef, exclusiveCoreLoadOwnerRef],
    );

    const refreshWatchlistSurface = useCallback(async ({
        requestId,
        matricesOverride = null,
        requireMatrixBackedRows = false,
        matrixStatus = 'not_requested',
        betaSource = currentBetaSource,
        securityOperationId = null,
        securityOperationType = null,
        expectedWatchlistRevisionId = null,
        expectedWatchlistRevisionSeq = null,
        stagePublicationState = null,
        metadataOnly = false,
        stagePresentationState = null,
    } = {}) => {
        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        if (!isCurrentRequest()) {
            return buildNotLoadedWatchlistSurface('cancelled');
        }
        const authoritativeEmpty = (
            requireMatrixBackedRows
            && !matricesOverride
            && matrixStatus === 'empty_watchlist'
        );
        if (requireMatrixBackedRows && !matricesOverride && !authoritativeEmpty) {
            return buildNotLoadedWatchlistSurface(matrixStatus);
        }
        const isWatchlistPublication = (
            securityOperationId != null
            && WATCHLIST_PUBLICATION_OPERATION_TYPES.has(
                String(securityOperationType || '').trim(),
            )
        );
        if (isWatchlistPublication && metadataOnly) {
            throw new Error('Watchlist publication cannot use metadata-only rows.');
        }
        const fetchOptions = {
            isCurrentRequest,
            requireMatrixBackedRows,
            betaSource,
            metadataOnly,
            authoritativeEmpty,
            ...(stagePresentationState ? { stagePresentationState } : {}),
            expectedWatchlistRevisionId,
            expectedWatchlistRevisionSeq,
            ...(typeof stagePublicationState === 'function' ? { stagePublicationState } : {}),
        };
        const response = await fetchIndustries(matricesOverride, fetchOptions);
        const resolvedRows = response?.rows;
        const watchlistRevision = response?.watchlistRevision ?? null;
        if (!isCurrentRequest()) {
            return buildNotLoadedWatchlistSurface('cancelled');
        }
        if (isWatchlistPublication) {
            if (
                typeof stagePublicationState !== 'function'
                || typeof fetchSecurityQuotesForPublication !== 'function'
            ) {
                throw new Error('Watchlist publication quote loader is not configured.');
            }
            if (resolvedRows?.length && !response?.watchlistFactorContextId) {
                throw new Error('Published Watchlist factor context is missing. Please refresh.');
            }
            const quoteResult = await fetchSecurityQuotesForPublication(resolvedRows, {
                isCurrentRequest,
                stagePublicationState,
                expectedWatchlistRevisionId,
                expectedWatchlistRevisionSeq,
                expectedWatchlistFactorContextId: response?.watchlistFactorContextId ?? null,
            });
            if (!isCurrentRequest()) {
                return buildNotLoadedWatchlistSurface('cancelled');
            }
            if (quoteResult?.publicationStateStaged !== true) {
                throw new Error('Watchlist publication quote snapshot was not staged.');
            }
        }
        return {
            ...buildWatchlistSurfaceResult({ rows: resolvedRows, watchlistRevision, matricesOverride, matrixStatus }),
            ...(stagePresentationState ? {
                preparedRows: resolvedRows,
                preparedFactorContextId: response?.watchlistFactorContextId ?? null,
            } : {}),
        };
    }, [
        currentBetaSource,
        fetchIndustries,
        fetchSecurityQuotesForPublication,
        isCoreLoadCurrent,
    ]);

    const refreshOptimizerSummarySurface = useCallback(async ({
        requestId,
        includeOptimizerSummaries = true,
    } = {}) => {
        if (!includeOptimizerSummaries) {
            return null;
        }
        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        if (!isCurrentRequest()) {
            return null;
        }
        const result = await fetchOptimizerPortfolioSummaries({ isCurrentRequest });
        if (!isCurrentRequest()) {
            return null;
        }
        return result;
    }, [fetchOptimizerPortfolioSummaries, isCoreLoadCurrent]);

    const refreshActualPortfolioPrimarySurface = useCallback(async ({
        requestId,
        actualId,
        matricesOverride = null,
        riskContextOverride = null,
        targetKey = 'actual',
        stagePublicationState = null,
        includeHoldings = true,
        generation = null,
        resourceScopeKey = null,
        stagePresentationState = null,
    } = {}) => {
        const portfolioId = actualId ?? null;
        if (portfolioId == null) {
            return null;
        }

        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        if (!isCurrentRequest()) {
            return false;
        }

        const positionsResult = await fetchPositions(
            portfolioId,
            {
                isCurrentRequest,
                riskContextOverride,
                generation,
                resourceScopeKey,
                ...(stagePresentationState ? { stagePresentationState } : {}),
                ...(typeof stagePublicationState === 'function' ? { stagePublicationState } : {}),
            },
        );
        if (!isCurrentRequest()) {
            return false;
        }
        if (positionsResult?.loaded !== true) {
            throw new Error('Actual portfolio positions did not load coherently.');
        }
        if (!includeHoldings) {
            return true;
        }

        const holdingsResult = await fetchHoldings(
            portfolioId,
            matricesOverride,
            targetKey,
            {
                isCurrentRequest,
                riskContextOverride,
                generation,
                resourceScopeKey,
                ...(stagePresentationState ? { stagePresentationState } : {}),
                ...(typeof stagePublicationState === 'function' ? { stagePublicationState } : {}),
            },
        );
        if (!isCurrentRequest()) {
            return false;
        }
        if (holdingsResult?.loaded !== true) {
            throw new Error('Actual portfolio holdings did not load coherently.');
        }
        return { positionsResult, holdingsResult };
    }, [
        fetchHoldings,
        fetchPositions,
        isCoreLoadCurrent,
    ]);

    const refreshActualPortfolioSecondarySurface = useCallback(async ({
        requestId,
        actualId,
        matricesOverride = null,
        riskContextOverride = null,
        targetKey = 'actual',
        stagePublicationState = null,
        generation = null,
        resourceScopeKey = null,
        stagePresentationState = null,
    } = {}) => {
        const portfolioId = actualId ?? null;
        if (portfolioId == null) {
            return null;
        }

        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        if (!isCurrentRequest()) {
            return false;
        }

        const groupResult = await fetchGroupRiskMetrics({
            portfolioId,
            targetKey,
            matricesOverride,
            riskContextOverride,
            isCurrentRequest,
            generation,
            resourceScopeKey,
            ...(stagePresentationState ? { stagePresentationState } : {}),
            ...(typeof stagePublicationState === 'function' ? { stagePublicationState } : {}),
        });
        return isCurrentRequest() ? { groupResult } : false;
    }, [
        fetchGroupRiskMetrics,
        isCoreLoadCurrent,
    ]);

    const refreshActualPortfolioSurface = useCallback(async (options = {}) => {
        const primaryLoaded = await refreshActualPortfolioPrimarySurface(options);
        if (!primaryLoaded) {
            return primaryLoaded;
        }
        const secondary = await refreshActualPortfolioSecondarySurface(options);
        return secondary ? { ...primaryLoaded, ...secondary } : false;
    }, [
        refreshActualPortfolioPrimarySurface,
        refreshActualPortfolioSecondarySurface,
    ]);

    const loadMatricesForRiskSurface = useCallback(async ({
        force = false,
        requestId = null,
        idioVolSource = currentIdioVolSource,
        betaSource = currentBetaSource,
        matrixScopeVersion = watchlistVersion,
        watchlistIdentityScopeVersion: loadWatchlistIdentityScopeVersion = watchlistIdentityScopeVersion,
        latestFactorDataVersionKey: loadLatestFactorDataVersionKey = latestFactorDataVersionKey,
        factorVendorOverride,
        factorDataModelOverride,
        factorSnapshotDateOverride,
        reason = 'init',
        preserveExistingOnMatrixFailure = false,
        stagePublicationState = null,
        stagePresentationState = null,
    } = {}) => {
        if (!userUid) {
            return {
                matricesOverride: null,
                matrixStatus: 'not_requested',
            };
        }

        const matrixContextKey = buildMatrixContextKey({
            userUid,
            selectedFactorModel,
            requestedFactorDataModel,
            factorVendorOverride,
            factorDataModelOverride,
            matrixScopeVersion,
            watchlistIdentityScopeVersion: loadWatchlistIdentityScopeVersion,
            latestFactorDataVersionKey: loadLatestFactorDataVersionKey,
            factorSnapshotDateOverride,
            factorSnapshotDate,
            idioVolSource,
            betaSource,
        });
        let matricesOverride = getCurrentFactorContext();

        if (force || matrixContextKeyRef.current !== matrixContextKey || !matricesOverride) {
            const fetchOptions = {
                idioVolSource,
                betaSource,
                forceWatchlistProbe: force || reason === 'watchlist_upload',
            };
            if (factorVendorOverride != null) {
                fetchOptions.factorVendorOverride = factorVendorOverride;
            }
            if (factorDataModelOverride != null) {
                fetchOptions.factorDataModelOverride = factorDataModelOverride;
            }
            if (factorSnapshotDateOverride !== undefined) {
                fetchOptions.factorSnapshotDateOverride = factorSnapshotDateOverride;
            }
            if (force && reason === 'factor_data_refresh') {
                fetchOptions.useLatestFactorSnapshot = true;
            }
            if (preserveExistingOnMatrixFailure) {
                fetchOptions.preserveExistingOnMatrixFailure = true;
            }
            if (typeof stagePublicationState === 'function') {
                fetchOptions.stagePublicationState = stagePublicationState;
                fetchOptions.isCurrentRequest = requestId == null
                    ? null
                    : () => isCoreLoadCurrent(requestId);
            }
            if (stagePresentationState) fetchOptions.stagePresentationState = stagePresentationState;
            const matrixWorkflowResult = await fetchFactorMatrices(fetchOptions);
            if (requestId != null && !isCoreLoadCurrent(requestId)) {
                return {
                    matricesOverride: null,
                    matrixStatus: 'cancelled',
                };
            }

            const {
                response: matricesRes,
                matrixStatus,
                matrixReason,
            } = normalizeMatrixWorkflowResult(matrixWorkflowResult);

            if (!matricesRes) {
                if (!preserveExistingOnMatrixFailure || matrixStatus === 'empty_watchlist') {
                    matrixContextKeyRef.current = '';
                }
                return {
                    matricesOverride: null,
                    matrixStatus,
                    matrixReason,
                };
            }

            if (matrixStatus !== 'ready') {
                if (!preserveExistingOnMatrixFailure) {
                    matrixContextKeyRef.current = '';
                }
                return {
                    matricesOverride: null,
                    matrixStatus,
                    matrixReason,
                };
            }

            // Publication stages the new settings until every surface is ready;
            // the rendered vendor can still belong to the previous revision.
            matricesOverride = buildFactorContextFromResponse(matricesRes, {
                factorModel: factorVendorOverride ?? selectedFactorModel,
                betaSource,
                idioVolSource,
            });
            if (!matricesOverride) {
                if (!preserveExistingOnMatrixFailure) {
                    matrixContextKeyRef.current = '';
                }
                return {
                    matricesOverride: null,
                    matrixStatus: 'unavailable',
                    matrixReason: 'invalid_matrix_context',
                };
            }
            const publishMatrixContextKey = () => {
                matrixContextKeyRef.current = matrixContextKey;
            };
            if (typeof stagePublicationState === 'function') {
                stagePublicationState(
                    publishMatrixContextKey,
                    requestId == null ? null : () => isCoreLoadCurrent(requestId),
                );
            } else {
                publishMatrixContextKey();
            }
        }

        return {
            matricesOverride,
            matrixStatus: matricesOverride ? 'ready' : 'unavailable',
        };
    }, [
        buildFactorContextFromResponse,
        fetchFactorMatrices,
        getCurrentFactorContext,
        isCoreLoadCurrent,
        latestFactorDataVersionKey,
        matrixContextKeyRef,
        currentIdioVolSource,
        currentBetaSource,
        factorSnapshotDate,
        requestedFactorDataModel,
        selectedFactorModel,
        userUid,
        watchlistIdentityScopeVersion,
        watchlistVersion,
    ]);

    const resolveSelectedOverviewSurfaceContext = useCallback(({
        requestId,
        betaSource = currentBetaSource,
    } = {}) => {
        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        const selectedOverviewPortfolioMeta = selectedSummaryPortfolioMeta ??
            listOfWhatIfPortfolios?.find(
                (portfolio) => String(portfolio?.id) === String(optimalPortfolioSummaryId),
            ) ??
            null;
        const selectedOverviewIsPosSizing = isPosSizingPortfolioSelection({
            portfolioId: optimalPortfolioSummaryId,
            portfolioName:
                selectedOverviewPortfolioMeta?.value
                ?? selectedOverviewPortfolioMeta?.name
                ?? optimalPortfolioSummary,
            portfolioType: selectedOverviewPortfolioMeta?.type ?? null,
        });
        const baseSelectedOverviewRiskContext = getPortfolioOverviewRiskContext(
            selectedOverviewIsPosSizing
                ? { ...selectedOverviewPortfolioMeta, type: 'wi' }
                : selectedOverviewPortfolioMeta,
        );
        const selectedOverviewRiskContext = baseSelectedOverviewRiskContext?.usesSavedSnapshot
            ? baseSelectedOverviewRiskContext
            : {
                ...baseSelectedOverviewRiskContext,
                betaSource,
            };
        if (!isCurrentRequest()) {
            return null;
        }

        const selectedOverviewTargetKey = selectedOverviewIsPosSizing ? 'pos' : 'opt';

        return {
            isCurrentRequest,
            selectedOverviewMatricesOverride: null,
            selectedOverviewPortfolioMeta,
            selectedOverviewRiskContext,
            selectedOverviewTargetKey,
        };
    }, [
        currentBetaSource,
        getPortfolioOverviewRiskContext,
        isCoreLoadCurrent,
        isPosSizingPortfolioSelection,
        listOfWhatIfPortfolios,
        optimalPortfolioSummary,
        optimalPortfolioSummaryId,
        selectedSummaryPortfolioMeta,
    ]);

    const refreshSelectedOverviewSurface = useCallback(async ({
        requestId,
        selectedOverviewRequestId = null,
        includeOptimized = false,
        betaSource = currentBetaSource,
        matrixScopeVersion = watchlistVersion,
        stagePublicationState = null,
        stagePresentationState = null,
        preparedScope = null,
        posSizingVersionOverride = posSizingVersion,
    } = {}) => {
        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        const selectedPortfolioId = preparedScope?.portfolioId ?? optimalPortfolioSummaryId;
        const ownsSelectedOverviewLoading = () => (
            selectedOverviewRequestId != null
            && selectedOverviewRefreshRequestIdRef.current === selectedOverviewRequestId
            && coreSelectedOverviewOwnerRef.current === selectedOverviewRequestId
        );
        const isCurrentSelectedRequest = () => (
            isCurrentRequest() && ownsSelectedOverviewLoading()
        );
        if (
            !includeOptimized
            || !selectedPortfolioId
            || !isCurrentSelectedRequest()
        ) {
            return false;
        }

        const resolved = preparedScope ? {
            isCurrentRequest,
            selectedOverviewMatricesOverride: null,
            selectedOverviewPortfolioMeta: preparedScope.portfolio,
            selectedOverviewRiskContext: preparedScope.riskContext,
            selectedOverviewTargetKey: preparedScope.targetKey,
        } : await resolveSelectedOverviewSurfaceContext({ requestId, betaSource });
        if (!resolved?.isCurrentRequest() || !isCurrentSelectedRequest()) {
            return false;
        }

        if (typeof stagePublicationState !== 'function') {
            throw new Error('Core selected Overview requires a publication transaction.');
        }
        // Read when the request starts: an invalidation dispatched just before
        // this load is newer than the version this callback was rendered with.
        const invalidationVersion = selectedOverviewInvalidationVersionRef.current;
        const resourceScopeKey = buildSelectedRiskOverviewScopeKey({
            userUid,
            portfolioId: selectedPortfolioId,
            portfolioRefreshToken: resolved.selectedOverviewPortfolioMeta?.lastRefreshedAt,
            watchlistVersion: matrixScopeVersion,
            posSizingVersion: posSizingVersionOverride,
            invalidationVersion,
            targetKey: resolved.selectedOverviewTargetKey,
            riskContext: resolved.selectedOverviewRiskContext,
        });
        const selectedPublication = createRiskOverviewSnapshotPublicationTransaction({
            requiredPanels: ['primary', 'group'],
            scopeKey: resourceScopeKey,
            generation: selectedOverviewRequestId,
            isCurrent: isCurrentSelectedRequest,
        });

        try {
            await fetchOptimizedRiskMetrics(
                selectedPortfolioId,
                resolved.selectedOverviewMatricesOverride,
                resolved.selectedOverviewRiskContext,
                {
                    isCurrentRequest: isCurrentSelectedRequest,
                    stagePublicationState: selectedPublication.stage,
                    generation: selectedOverviewRequestId,
                    targetKey: resolved.selectedOverviewTargetKey,
                    resourceScopeKey,
                    ...(stagePresentationState ? { stagePresentationState } : {}),
                },
            );

            if (!isCurrentSelectedRequest()) {
                selectedPublication.discard();
                return false;
            }
            await fetchGroupRiskMetrics({
                portfolioId: selectedPortfolioId,
                targetKey: resolved.selectedOverviewTargetKey,
                matricesOverride: resolved.selectedOverviewMatricesOverride,
                riskContextOverride: resolved.selectedOverviewRiskContext,
                isCurrentRequest: isCurrentSelectedRequest,
                stagePublicationState: selectedPublication.stage,
                generation: selectedOverviewRequestId,
                resourceScopeKey,
                ...(stagePresentationState ? { stagePresentationState } : {}),
            });

            if (!isCurrentSelectedRequest()) {
                selectedPublication.discard();
                return false;
            }
            if (!selectedPublication.canCommit()) {
                throw new Error('Selected Overview panels did not load coherently.');
            }
            const publishSelectedOverview = () => {
                if (!selectedPublication.commit()) return;
                setOptimalPortfolioSummaryLoaded?.(true);
                if (!lastSeenOptimizedOverviewRefreshRef) return;
                lastSeenOptimizedOverviewRefreshRef.current = {
                    id: selectedPortfolioId,
                    token: resolved.selectedOverviewPortfolioMeta?.lastRefreshedAt ?? null,
                    contextKey: buildSelectedOverviewRefreshContextKey({
                        targetKey: resolved.selectedOverviewTargetKey,
                        watchlistVersion: matrixScopeVersion,
                        posSizingVersion: posSizingVersionOverride,
                        invalidationVersion,
                        riskContext: resolved.selectedOverviewRiskContext,
                    }),
                    inFlightKey: '',
                    userUid,
                };
            };
            stagePublicationState(publishSelectedOverview, selectedPublication.canCommit);
            return true;
        } catch (error) {
            selectedPublication.discard();
            throw error;
        }
    }, [
        coreSelectedOverviewOwnerRef,
        currentBetaSource,
        fetchOptimizedRiskMetrics,
        fetchGroupRiskMetrics,
        isCoreLoadCurrent,
        lastSeenOptimizedOverviewRefreshRef,
        optimalPortfolioSummaryId,
        posSizingVersion,
        resolveSelectedOverviewSurfaceContext,
        selectedOverviewRefreshRequestIdRef,
        selectedOverviewInvalidationVersionRef,
        setOptimalPortfolioSummaryLoaded,
        userUid,
        watchlistVersion,
    ]);

    const readPriceRefreshStatusRaw = useCallback(async ({
        silent = false,
        sinceUpdatedAt = lastAppliedPriceWatermarkRef.current,
    } = {}) => {
        if (!userUid) {
            return {
                ok: true,
                status: null,
            };
        }

        const singleFlightKey = buildRiskRequestKey([
            'fetch_price_refresh_status',
            userUid,
            sinceUpdatedAt ?? '',
            factorSnapshotDate ?? '',
        ]);

        try {
            const result = await runSingleFlight(singleFlightKey, () => callBackend(
                'fetch_price_refresh_status',
                'POST',
                {
                    since_updated_at: sinceUpdatedAt,
                    since_factor_snapshot_date: factorSnapshotDate,
                },
            ));

            return {
                ok: true,
                status: normalizePriceRefreshStatusPayload(result),
            };
        } catch (error) {
            if (!silent) {
                console.error('Error fetching price refresh status:', error);
            }
            return {
                ok: false,
                status: null,
            };
        }
    }, [factorSnapshotDate, lastAppliedPriceWatermarkRef, runSingleFlight, userUid]);

    const finishRiskSurfaceLoad = useCallback(async ({
        requestId,
        initialPriceRefreshRead,
        stagePublicationState = null,
    } = {}) => {
        if (!isCoreLoadCurrent(requestId)) {
            return {
                priceRefreshStatus: null,
                watermarkAdvancedDuringLoad: false,
                cancelled: true,
            };
        }

        const postLoadPriceRefreshRead = await readPriceRefreshStatusRaw({
            silent: true,
            sinceUpdatedAt: initialPriceRefreshRead.ok
                ? initialPriceRefreshRead.status?.latest_security_updated_at ?? null
                : null,
        });
        if (!isCoreLoadCurrent(requestId)) {
            return {
                priceRefreshStatus: null,
                watermarkAdvancedDuringLoad: false,
                cancelled: true,
            };
        }

        const watermarkCheckReliable = initialPriceRefreshRead.ok && postLoadPriceRefreshRead.ok;
        const postLoadPriceRefreshStatus = postLoadPriceRefreshRead.status
            ? normalizePriceRefreshStatusPayload(postLoadPriceRefreshRead.status, {
                hasNewerPrices: watermarkCheckReliable
                    ? postLoadPriceRefreshRead.status?.has_newer_prices
                    : false,
            })
            : null;
        const watermarkAdvancedDuringLoad = Boolean(
            watermarkCheckReliable && postLoadPriceRefreshRead.status?.has_newer_prices,
        );

        if (postLoadPriceRefreshStatus) {
            const publishPriceStatus = () => {
                setPriceRefreshStatus(postLoadPriceRefreshStatus);
                if (!watermarkAdvancedDuringLoad) {
                    lastAppliedPriceWatermarkRef.current = postLoadPriceRefreshStatus.latest_security_updated_at;
                    lastPendingPriceWatermarkRef.current = null;
                    setPendingAutoRefresh(null);
                }
            };
            if (typeof stagePublicationState === 'function') {
                stagePublicationState(
                    publishPriceStatus,
                    () => isCoreLoadCurrent(requestId),
                );
            } else {
                publishPriceStatus();
            }
        }

        return {
            priceRefreshStatus: postLoadPriceRefreshStatus,
            watermarkAdvancedDuringLoad,
            cancelled: false,
        };
    }, [
        isCoreLoadCurrent,
        lastAppliedPriceWatermarkRef,
        lastPendingPriceWatermarkRef,
        readPriceRefreshStatusRaw,
        setPendingAutoRefresh,
        setPriceRefreshStatus,
    ]);

    const completeDeferredStartupLoad = useCallback(async ({
        requestId,
        actualId,
        matricesOverride,
        includeOptimizerSummaries,
        matrixStatus,
        matrixReason,
        watchlistSurface,
    }) => {
        await waitForDeferredStartup();
        if (!isCoreLoadCurrent(requestId)) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }

        const initialPriceRefreshPromise = readPriceRefreshStatusRaw({
            sinceUpdatedAt: null,
            silent: true,
        });
        await Promise.allSettled([
            refreshOptimizerSummarySurface({
                requestId,
                includeOptimizerSummaries:
                    includeOptimizerSummaries
                    && matrixStatus !== 'empty_watchlist'
                    && watchlistSurface?.source !== 'empty',
            }),
        ]);
        if (!isCoreLoadCurrent(requestId)) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }

        const initialPriceRefreshRead = await initialPriceRefreshPromise;
        const finishResult = await finishRiskSurfaceLoad({
            requestId,
            initialPriceRefreshRead,
        });
        if (finishResult.cancelled) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }

        return buildCoreLoadResult({
            actualId,
            matricesOverride,
            priceRefreshStatus: finishResult.priceRefreshStatus,
            watermarkAdvancedDuringLoad: finishResult.watermarkAdvancedDuringLoad,
            matrixStatus,
            matrixReason,
            watchlistSurface,
        });
    }, [
        finishRiskSurfaceLoad,
        isCoreLoadCurrent,
        readPriceRefreshStatusRaw,
        refreshOptimizerSummarySurface,
    ]);

    const loadCoreData = useCallback(async ({
        reason = 'init',
        targetKey = 'actual',
        idioVolSource = currentIdioVolSource,
        betaSource = currentBetaSource,
        includeOptimized = false,
        forceMatricesReload = false,
        matrixScopeVersion = watchlistVersion,
        watchlistIdentityScopeVersion: loadWatchlistIdentityScopeVersion = watchlistIdentityScopeVersion,
        latestFactorDataVersionKey: loadLatestFactorDataVersionKey = latestFactorDataVersionKey,
        positionsVersion = actualPortfolioVersion,
        includeOptimizerSummaries = true,
        deferSecondary = false,
        preserveExistingOnMatrixFailure = false,
        factorVendorOverride,
        factorDataModelOverride,
        factorSnapshotDateOverride,
        securityOperationId = null,
        securityOperationType = null,
        expectedWatchlistRevisionId = null,
        expectedWatchlistRevisionSeq = null,
        exclusiveCoreLoadOwner = null,
        publicationVersionTransition = null,
        preparePublication = false,
        deferSelectedOverview = false,
        stagePresentationState = null,
    } = {}) => {
        if (!userUid) {
            return buildCoreLoadResult();
        }

        if (preparePublication && (!publicationVersionTransition || !exclusiveCoreLoadOwner
            || typeof stagePresentationState !== 'function')) {
            throw new Error('Prepared Core publication requires exact owner, version plan and presentation staging.');
        }
        const ownsRequestedCoreLoad = exclusiveCoreLoadOwner
            ? exclusiveCoreLoadOwnerRef?.current === exclusiveCoreLoadOwner
            : !exclusiveCoreLoadOwnerRef?.current;
        if (!ownsRequestedCoreLoad) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }

        if (publicationVersionTransition && (
            !Number.isSafeInteger(publicationVersionTransition.watchlistVersion)
            || !Number.isSafeInteger(publicationVersionTransition.actualPortfolioVersion)
            || typeof publicationVersionTransition.isCurrent !== 'function'
            || typeof publicationVersionTransition.commit !== 'function'
        )) throw new Error('Core publication resource version transition is invalid.');
        if (publicationVersionTransition && !publicationVersionTransition.isCurrent()) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }
        const resourceWatchlistVersion = publicationVersionTransition?.watchlistVersion ?? matrixScopeVersion;
        const resourceActualVersion = publicationVersionTransition?.actualPortfolioVersion ?? positionsVersion;
        const requestId = ++coreLoadRequestIdRef.current;
        coreLoadRequestOwnerRef.current = {
            requestId,
            owner: exclusiveCoreLoadOwner,
        };
        const publicationStateTransaction = createPublicationStateTransaction();
        const stagePublicationState = publicationStateTransaction.stage;
        if (publicationVersionTransition) {
            stagePublicationState(publicationVersionTransition.commit, publicationVersionTransition.isCurrent);
        }
        let publicationStateCommitted = false;
        let publicationStatePrepared = false;
        let actualRiskPublication = null;
        let preparedActualSurface = null;
        const selectedOverviewRequestId = (
            includeOptimized && optimalPortfolioSummaryId
        ) ? ++selectedOverviewRefreshRequestIdRef.current : null;
        if (selectedOverviewRequestId != null) {
            coreSelectedOverviewOwnerRef.current = selectedOverviewRequestId;
            if (preparePublication) {
                stagePublicationState(() => setOptimalPortfolioSummaryLoaded?.(false), () => isCoreLoadCurrent(requestId));
            } else setOptimalPortfolioSummaryLoaded?.(false);
        }
        const releaseSelectedOverviewOwner = () => {
            if (
                selectedOverviewRequestId != null
                && coreSelectedOverviewOwnerRef.current === selectedOverviewRequestId
            ) {
                coreSelectedOverviewOwnerRef.current = null;
            }
        };
        const isCurrentRequest = () => isCoreLoadCurrent(requestId);
        const commitPublicationState = (displayFactorContext = undefined, mandatoryReadiness = null) => {
            if (publicationVersionTransition && !isReadyWatchlistPublicationRefreshResult(mandatoryReadiness)) return false;
            if (!publicationStateTransaction.commit(isCurrentRequest)) {
                return false;
            }
            publicationStateCommitted = true;
            if (displayFactorContext !== undefined && typeof onCoreFactorContextCommitted === 'function') {
                try {
                    onCoreFactorContextCommitted({ factorContext: displayFactorContext, isCurrentRequest });
                } catch (error) {
                    // Display metadata must not turn an already committed
                    // financial publication into a failed parent operation.
                    console.warn('Could not update the loaded factor label:', error);
                }
            }
            return true;
        };
        const preparedResult = (displayContext, readiness, fields) => {
            publicationStatePrepared = true;
            const canCommit = () => isReadyWatchlistPublicationRefreshResult(readiness)
                && publicationStateTransaction.canCommit(isCurrentRequest);
            const discard = () => {
                publicationStateTransaction.discard();
                actualRiskPublication?.discard();
                releaseSelectedOverviewOwner();
            };
            return buildCoreLoadResult({ ...fields,
                preparedState: {
                    actualPortfolioIsReady: fields.actualId != null
                        && (preparedActualSurface?.positionsResult?.userPositions?.length ?? 0) > 0,
                    watchlistIsReady: fields.watchlistSurface?.source === 'matrix'
                        && fields.watchlistSurface?.riskLoaded === true,
                    actualSurface: preparedActualSurface,
                    watchlistRows: fields.watchlistSurface?.preparedRows ?? [],
                    watchlistFactorContextId: fields.watchlistSurface?.preparedFactorContextId ?? null,
                },
                prepareSelectedOverview: async (preparedScope, targetPosSizingVersion) => {
                    if (!canCommit()) return false;
                    return refreshSelectedOverviewSurface({
                        requestId, selectedOverviewRequestId,
                        includeOptimized: includeOptimized && fields.matrixStatus === 'ready',
                        betaSource, matrixScopeVersion: resourceWatchlistVersion,
                        posSizingVersionOverride: targetPosSizingVersion,
                        preparedScope, stagePublicationState, stagePresentationState,
                    });
                },
                prepared: {
                canCommit,
                transferTo: (stage) => {
                    if (!canCommit()) return false;
                    publicationStateTransaction.stage(() => {
                        publicationStateCommitted = true;
                        if (displayContext !== undefined && typeof onCoreFactorContextCommitted === 'function') {
                            try { onCoreFactorContextCommitted({ factorContext: displayContext, isCurrentRequest }); }
                            catch (error) { console.warn('Could not update the loaded factor label:', error); }
                        }
                        releaseSelectedOverviewOwner();
                    }, isCurrentRequest);
                    return publicationStateTransaction.transferTo(stage);
                },
                commit: () => {
                    if (!canCommit()) { discard(); return false; }
                    const committed = commitPublicationState(displayContext, readiness);
                    releaseSelectedOverviewOwner();
                    return committed;
                },
                discard,
            } });
        };
        try {
        const actualIdPromise = fetchActualPortfolioId({
            isCurrentRequest,
            stagePublicationState,
            ...(preparePublication ? { stagePresentationState } : {}),
        });
        const matrixLoadPromise = loadMatricesForRiskSurface({
            force: forceMatricesReload,
            requestId,
            idioVolSource,
            betaSource,
            matrixScopeVersion,
            watchlistIdentityScopeVersion: loadWatchlistIdentityScopeVersion,
            latestFactorDataVersionKey: loadLatestFactorDataVersionKey,
            factorVendorOverride,
            factorDataModelOverride,
            factorSnapshotDateOverride,
            reason,
            preserveExistingOnMatrixFailure,
            stagePublicationState,
            ...(preparePublication ? { stagePresentationState } : {}),
        });

        let initialPriceRefreshRead = null;
        let actualId;
        let matrixLoad;
        if (deferSecondary) {
            [actualId, matrixLoad] = await Promise.all([
                actualIdPromise,
                matrixLoadPromise,
            ]);
        } else {
            [initialPriceRefreshRead, actualId, matrixLoad] = await Promise.all([
                readPriceRefreshStatusRaw({
                    sinceUpdatedAt: null,
                    silent: true,
                }),
                actualIdPromise,
                matrixLoadPromise,
            ]);
        }
        if (!isCurrentRequest()) {
            return buildCoreLoadResult({
                matrixStatus: 'cancelled',
            });
        }

        const matricesOverride = matrixLoad?.matricesOverride ?? null;
        const matrixStatus = matrixLoad?.matrixStatus ?? (matricesOverride ? 'ready' : 'unavailable');
        const matrixReason = matrixLoad?.matrixReason ?? null;
        const riskDataAvailable = matrixStatus === 'ready' && Boolean(matricesOverride);
        const actualRiskContext = riskDataAvailable
            ? {
                factorModel: matricesOverride?.factorModel ?? selectedFactorModel,
                factorDataModel:
                    matricesOverride?.factorDataModel ?? requestedFactorDataModel,
                factorSnapshotDate:
                    matricesOverride?.factorSnapshotDate ?? factorSnapshotDate,
                betaSource,
                idioVolSource,
            }
            : null;
        const actualResourceScopeKey = actualId != null && actualRiskContext
            ? buildRiskOverviewScopeKey(buildPortfolioRiskOverviewScope({
                userUid,
                portfolioId: actualId,
                actualPortfolioVersion: resourceActualVersion,
                watchlistVersion: resourceWatchlistVersion,
                targetKey,
                ...actualRiskContext,
            }))
            : null;
        const actualResourceGeneration = requestId;
        const createActualRiskPublication = () => createRiskOverviewSnapshotPublicationTransaction({
            requiredPanels: ['positions', 'primary', 'group'],
            scopeKey: actualResourceScopeKey,
            generation: actualResourceGeneration,
            isCurrent: isCurrentRequest,
        });
        if (actualResourceScopeKey) {
            actualRiskPublication = createActualRiskPublication();
        }
        if (actualId === undefined) {
            throw new Error('Actual portfolio identity did not load coherently.');
        }
        const requiresMatrixBackedWatchlistRows = reason === 'watchlist_upload' || forceMatricesReload;
        if (matrixStatus === 'empty_watchlist') {
            const watchlistSurface = await refreshWatchlistSurface({
                requestId,
                matricesOverride: null,
                requireMatrixBackedRows: requiresMatrixBackedWatchlistRows,
                matrixStatus,
                betaSource,
                securityOperationId,
                securityOperationType,
                expectedWatchlistRevisionId,
                expectedWatchlistRevisionSeq,
                stagePublicationState,
            });
            if (!isCurrentRequest()) {
                return buildCoreLoadResult({ matrixStatus: 'cancelled' });
            }
            if (watchlistSurface.source !== 'empty') {
                throw new Error('Empty watchlist publication did not load coherently.');
            }
            if (typeof clearActualRiskOverviewState !== 'function') {
                throw new Error('Actual Risk Overview clearing contract is required.');
            }
            stagePublicationState(clearActualRiskOverviewState, isCurrentRequest);
            if (publicationVersionTransition && !isReadyWatchlistPublicationRefreshResult({
                matrixStatus, matricesOverride: null, watchlistSurface,
            })) return buildCoreLoadResult({ actualId, matrixStatus, matrixReason, watchlistSurface });
            if (preparePublication) return preparedResult(null,
                { matrixStatus, matricesOverride: null, watchlistSurface },
                { actualId, matricesOverride: null, matrixStatus, matrixReason, watchlistSurface });
            if (!commitPublicationState(null, { matrixStatus, matricesOverride: null, watchlistSurface })) {
                return buildCoreLoadResult({ matrixStatus: 'cancelled' });
            }
            return buildCoreLoadResult({
                actualId,
                matricesOverride: null,
                matrixStatus,
                matrixReason,
                watchlistSurface,
            });
        }
        if (actualId === null) {
            if (typeof clearActualRiskOverviewState !== 'function') {
                throw new Error('Actual Risk Overview clearing contract is required.');
            }
            stagePublicationState(clearActualRiskOverviewState, isCurrentRequest);
        }
        if (preserveExistingOnMatrixFailure && forceMatricesReload && matrixStatus !== 'ready') {
            const watchlistSurface = securityOperationId != null
                ? await refreshWatchlistSurface({
                    requestId,
                    matricesOverride: null,
                    requireMatrixBackedRows: true,
                    matrixStatus,
                    betaSource,
                    securityOperationId,
                    securityOperationType,
                    expectedWatchlistRevisionId,
                    expectedWatchlistRevisionSeq,
                    stagePublicationState,
                })
                : buildNotLoadedWatchlistSurface(matrixStatus);
            if (!isCurrentRequest()) {
                return buildCoreLoadResult({
                    matrixStatus: 'cancelled',
                });
            }
            if (!publicationVersionTransition && actualId === null) {
                if (!commitPublicationState()) {
                    return buildCoreLoadResult({ matrixStatus: 'cancelled' });
                }
            }
            return buildCoreLoadResult({
                actualId,
                matricesOverride: null,
                matrixStatus,
                matrixReason,
                watchlistSurface,
            });
        }

        let watchlistSurface;
        if (deferSecondary) {
            [watchlistSurface, preparedActualSurface] = await Promise.all([
                refreshWatchlistSurface({
                    requestId,
                    matricesOverride,
                    requireMatrixBackedRows: requiresMatrixBackedWatchlistRows,
                    matrixStatus,
                    betaSource,
                    securityOperationId,
                    securityOperationType,
                    expectedWatchlistRevisionId,
                    expectedWatchlistRevisionSeq,
                    stagePublicationState,
                    ...(preparePublication ? { stagePresentationState } : {}),
                    metadataOnly: !riskDataAvailable,
                }),
                actualRiskPublication
                    ? refreshActualPortfolioSurface({
                        requestId,
                        actualId,
                        matricesOverride,
                        riskContextOverride: actualRiskContext,
                        targetKey,
                        stagePublicationState: actualRiskPublication.stage,
                        generation: actualResourceGeneration,
                        resourceScopeKey: actualResourceScopeKey,
                        ...(preparePublication ? { stagePresentationState } : {}),
                    })
                    : refreshActualPortfolioPrimarySurface({
                        requestId,
                        actualId,
                        matricesOverride: null,
                        targetKey,
                        stagePublicationState,
                        includeHoldings: false,
                    }),
            ]);
        } else {
            watchlistSurface = await refreshWatchlistSurface({
                requestId,
                matricesOverride,
                requireMatrixBackedRows: requiresMatrixBackedWatchlistRows,
                matrixStatus,
                betaSource,
                securityOperationId,
                securityOperationType,
                expectedWatchlistRevisionId,
                expectedWatchlistRevisionSeq,
                stagePublicationState,
                ...(preparePublication ? { stagePresentationState } : {}),
                metadataOnly: !riskDataAvailable,
            });
        }

        if (!isCurrentRequest()) {
            return buildCoreLoadResult({
                matrixStatus: 'cancelled',
            });
        }
        if (
            preserveExistingOnMatrixFailure
            && requiresMatrixBackedWatchlistRows
            && (
                matrixStatus !== 'ready'
                || watchlistSurface?.source !== 'matrix'
                || watchlistSurface?.riskLoaded !== true
            )
        ) {
            throw new Error('Matrix-backed watchlist rows did not load coherently.');
        }

        if (publicationVersionTransition && !isReadyWatchlistPublicationRefreshResult({
            matrixStatus, matricesOverride, watchlistSurface,
        })) return buildCoreLoadResult({ actualId, matricesOverride, matrixStatus, matrixReason, watchlistSurface });

        if (!deferSecondary) {
            await refreshOptimizerSummarySurface({
                requestId,
                includeOptimizerSummaries:
                    includeOptimizerSummaries
                    && matrixStatus !== 'empty_watchlist'
                    && watchlistSurface?.source !== 'empty',
            });

            if (!isCurrentRequest()) {
                return buildCoreLoadResult({
                    matrixStatus: 'cancelled',
                });
            }

            if (riskDataAvailable) {
                preparedActualSurface = await refreshActualPortfolioSurface({
                    requestId,
                    actualId,
                    matricesOverride,
                    riskContextOverride: actualRiskContext,
                    targetKey,
                    stagePublicationState: actualRiskPublication?.stage,
                    generation: actualResourceGeneration,
                    resourceScopeKey: actualResourceScopeKey,
                    ...(preparePublication ? { stagePresentationState } : {}),
                });
            } else {
                await refreshActualPortfolioPrimarySurface({
                    requestId,
                    actualId,
                    matricesOverride: null,
                    targetKey,
                    stagePublicationState,
                    includeHoldings: false,
                });
            }

            if (!isCurrentRequest()) {
                return buildCoreLoadResult({
                    matrixStatus: 'cancelled',
                });
            }
        }

        if (actualRiskPublication) {
            // Separate HTTP reads may observe different valid snapshots. Retry
            // only complete, current panels with nonempty but mixed tokens;
            // malformed/missing metadata and ownership failures still refuse.
            // The Watchlist, quotes, matrices and resource plan are not replayed.
            for (
                let attempt = 1;
                attempt < MAX_ACTUAL_SNAPSHOT_ATTEMPTS
                    && actualRiskPublication.hasSnapshotMismatch();
                attempt += 1
            ) {
                if (
                    !isCurrentRequest()
                    || (publicationVersionTransition && !publicationVersionTransition.isCurrent())
                ) {
                    return buildCoreLoadResult({ matrixStatus: 'cancelled' });
                }
                actualRiskPublication.discard();
                actualRiskPublication = createActualRiskPublication();
                preparedActualSurface = await refreshActualPortfolioSurface({
                    requestId,
                    actualId,
                    matricesOverride,
                    riskContextOverride: actualRiskContext,
                    targetKey,
                    stagePublicationState: actualRiskPublication.stage,
                    generation: actualResourceGeneration,
                    resourceScopeKey: actualResourceScopeKey,
                    ...(preparePublication ? { stagePresentationState } : {}),
                });
                if (
                    !isCurrentRequest()
                    || (publicationVersionTransition && !publicationVersionTransition.isCurrent())
                ) {
                    return buildCoreLoadResult({ matrixStatus: 'cancelled' });
                }
            }
            if (!actualRiskPublication.canCommit()) {
                throw new Error('Actual Risk Overview panels did not load coherently.');
            }
            stagePublicationState(
                actualRiskPublication.commit,
                actualRiskPublication.canCommit,
            );
        }

        if (!deferSelectedOverview) await refreshSelectedOverviewSurface({
            requestId,
            selectedOverviewRequestId,
            includeOptimized: includeOptimized && riskDataAvailable,
            betaSource,
            matrixScopeVersion: resourceWatchlistVersion,
            stagePublicationState,
            ...(preparePublication ? { stagePresentationState } : {}),
        });
        if (!isCurrentRequest()) {
            return buildCoreLoadResult({
                matrixStatus: 'cancelled',
            });
        }

        if (preparePublication) return preparedResult(riskDataAvailable ? matricesOverride : undefined,
            { matrixStatus, matricesOverride, watchlistSurface },
            { actualId, matricesOverride, matrixStatus, matrixReason, watchlistSurface });

        if (deferSecondary) {
            if (!commitPublicationState(riskDataAvailable ? matricesOverride : undefined, { matrixStatus, matricesOverride, watchlistSurface })) {
                return buildCoreLoadResult({ matrixStatus: 'cancelled' });
            }
            const deferredCompletion = completeDeferredStartupLoad({
                requestId,
                actualId,
                matricesOverride,
                includeOptimizerSummaries,
                matrixStatus,
                matrixReason,
                watchlistSurface,
            });
            return buildCoreLoadResult({
                actualId,
                matricesOverride,
                matrixStatus,
                matrixReason,
                watchlistSurface,
                deferredCompletion,
            });
        }

        const finishResult = await finishRiskSurfaceLoad({
            requestId,
            initialPriceRefreshRead,
            stagePublicationState,
            ...(preparePublication ? { stagePresentationState } : {}),
        });
        if (finishResult.cancelled) {
            return buildCoreLoadResult({
                matrixStatus: 'cancelled',
            });
        }
        if (!commitPublicationState(riskDataAvailable ? matricesOverride : undefined, { matrixStatus, matricesOverride, watchlistSurface })) {
            return buildCoreLoadResult({ matrixStatus: 'cancelled' });
        }

        return buildCoreLoadResult({
            actualId,
            matricesOverride,
            priceRefreshStatus: finishResult.priceRefreshStatus,
            watermarkAdvancedDuringLoad: finishResult.watermarkAdvancedDuringLoad,
            matrixStatus,
            matrixReason,
            watchlistSurface,
        });
        } finally {
            if (!publicationStateCommitted && !publicationStatePrepared) {
                publicationStateTransaction.discard();
                actualRiskPublication?.discard();
            }
            if (!publicationStatePrepared) releaseSelectedOverviewOwner();
        }
    }, [
        actualPortfolioVersion,
        completeDeferredStartupLoad,
        coreSelectedOverviewOwnerRef,
        coreLoadRequestIdRef,
        clearActualRiskOverviewState,
        currentIdioVolSource,
        currentBetaSource,
        exclusiveCoreLoadOwnerRef,
        factorSnapshotDate,
        finishRiskSurfaceLoad,
        fetchActualPortfolioId,
        isCoreLoadCurrent,
        latestFactorDataVersionKey,
        loadMatricesForRiskSurface,
        optimalPortfolioSummaryId,
        onCoreFactorContextCommitted,
        readPriceRefreshStatusRaw,
        requestedFactorDataModel,
        refreshActualPortfolioSurface,
        refreshActualPortfolioPrimarySurface,
        refreshOptimizerSummarySurface,
        refreshSelectedOverviewSurface,
        refreshWatchlistSurface,
        selectedOverviewRefreshRequestIdRef,
        setOptimalPortfolioSummaryLoaded,
        selectedFactorModel,
        userUid,
        watchlistIdentityScopeVersion,
        watchlistVersion,
    ]);

    return {
        readPriceRefreshStatusRaw,
        loadCoreData,
    };
}
